import {Action, State, StateContext} from '@ngxs/store';
import {ShareDeviceFormModel} from '@store/share-device-form/share-device-form.model';
import {Injectable} from '@angular/core';
import {
  DisableSliderLoader,
  EnableSliderLoader,
  FetchDevicesToShare, GetSharedDeviceById, GetSharedPhotosByDeviceId, ResetSharedFormState,
  SearchDevicesToShare,
  SetDeviceToShare, SetMapCoordinates,
  SetPageDevicesToShare, SortDeviceToShare, UploadSharedDevicesPhotos
} from '@store/share-device-form/share-device-form.actions';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {IResponse} from '@interfaces/response';
import {IDevice} from '@interfaces/devices';
import {SharedDevicesService} from '@services/shared-devices/shared-devices.service';
import {ISharedDevicePhoto, ISingleSharedDevice} from '@interfaces/shared-devices';

@State<ShareDeviceFormModel>({
  name: 'shareDeviceFormState',
  defaults: {
    deviceToShare: {
      currentPage: 1,
      searchParam: '',
      devices: null,
      itemsPerPage: 8,
      loader: false,
      sort: 'updatedAt',
      order: 'DESC',
    },
    activeDeviceId: null,
    activeDeviceName: '',
    maxSlidesNum: 11,
    slidesPhotos: [],
    activeDevice: null,
    sliderLoader: false,
    mapCoordinates: {
      address: '',
      lat: null,
      long: null,
    }
  }
})

@Injectable()
export class ShareDeviceFormState {
  constructor(private sharedDevicesService: SharedDevicesService) {
  }

  @Action(FetchDevicesToShare, {cancelUncompleted: true})
  fetchDevicesToShare(ctx: StateContext<ShareDeviceFormModel>): Observable<IResponse<IDevice>> {
    const limit = ctx.getState().deviceToShare.itemsPerPage;
    const skip = limit * (ctx.getState().deviceToShare.currentPage - 1);
    const search = ctx.getState().deviceToShare.searchParam;
    const order = ctx.getState().deviceToShare.order;
    const sort = ctx.getState().deviceToShare.sort;

    ctx.patchState({deviceToShare: {...ctx.getState().deviceToShare, loader: true}});

    return this.sharedDevicesService.getDevicesForSharing({
      limit,
      skip,
      search,
      order,
      sort
    }).pipe(
      tap((devices: IResponse<IDevice>) => {
        const state = ctx.getState();
        ctx.setState({...state, deviceToShare: {...state.deviceToShare, devices, loader: false}});
      }),
    );
  }

  @Action(SetPageDevicesToShare)
  setPageDevicesToShare(ctx: StateContext<ShareDeviceFormModel>, {page}: SetPageDevicesToShare): Observable<any> {
    ctx.patchState({deviceToShare: {...ctx.getState().deviceToShare, currentPage: page}});
    return ctx.dispatch(new FetchDevicesToShare());
  }

  @Action(SearchDevicesToShare)
  searchDevicesToShare(ctx: StateContext<ShareDeviceFormModel>, {search}: SearchDevicesToShare): Observable<any> {
    ctx.patchState({deviceToShare: {...ctx.getState().deviceToShare, searchParam: search, currentPage: 1}});
    return ctx.dispatch(new FetchDevicesToShare());
  }

  @Action(SetDeviceToShare)
  setDeviceToShare(ctx: StateContext<ShareDeviceFormModel>, {payload}: SetDeviceToShare): void {
    ctx.patchState({activeDeviceId: payload.id, activeDeviceName: payload.name || ctx.getState().activeDeviceName});
  }

  @Action(UploadSharedDevicesPhotos)
  uploadSharedDevicesPhotos(ctx: StateContext<ShareDeviceFormModel>,
                            {payload}: UploadSharedDevicesPhotos): Observable<IResponse<ISharedDevicePhoto>> {
    ctx.dispatch(new EnableSliderLoader());
    return this.sharedDevicesService.uploadPhotosSharedDevice(payload.photos, payload.id)
      .pipe(
        tap((photos) => {
          ctx.dispatch(new DisableSliderLoader());
          ctx.patchState({slidesPhotos: photos.rows});
        })
      );
  }

  @Action(GetSharedPhotosByDeviceId)
  getSharedPhotosByDeviceId(ctx: StateContext<ShareDeviceFormModel>,
                            {deviceId}: GetSharedPhotosByDeviceId): Observable<IResponse<ISharedDevicePhoto>> {
    ctx.dispatch(new EnableSliderLoader());
    return this.sharedDevicesService.getSharedPhotosByDeviceId(deviceId)
      .pipe(
        tap((photos) => {
          ctx.dispatch(new DisableSliderLoader());
          ctx.patchState({slidesPhotos: photos.rows});
        })
      );
  }

  @Action(GetSharedDeviceById)
  getSharedDeviceById(ctx: StateContext<ShareDeviceFormModel>, {id}: GetSharedDeviceById): Observable<ISingleSharedDevice> {
    return this.sharedDevicesService.getSharedDeviceById(id)
      .pipe(
        tap((device) => {
          ctx.patchState({
            activeDevice: device, activeDeviceId: device.id,
            activeDeviceName: device.name, mapCoordinates: {address: device.location, long: device.locationLng, lat: device.locationLat}
          });
        })
      );
  }

  @Action(EnableSliderLoader)
  enableSliderLoader(ctx: StateContext<ShareDeviceFormModel>): void {
    ctx.patchState({sliderLoader: true});
  }

  @Action(DisableSliderLoader)
  disableSliderLoader(ctx: StateContext<ShareDeviceFormModel>): void {
    ctx.patchState({sliderLoader: false});
  }

  @Action(SetMapCoordinates)
  setMapCoordinates(ctx: StateContext<ShareDeviceFormModel>, {coordinates}: SetMapCoordinates): void {
    ctx.patchState({mapCoordinates: coordinates});
  }

  @Action(SortDeviceToShare)
  sortDevicesToShare(ctx: StateContext<ShareDeviceFormModel>, {payload}: SortDeviceToShare): Observable<any> {
    ctx.patchState({deviceToShare: {...ctx.getState().deviceToShare, sort: payload.sort, order: payload.order}});
    return ctx.dispatch(new FetchDevicesToShare());
  }

  @Action(ResetSharedFormState)
  resetSharedFormState(ctx: StateContext<ShareDeviceFormModel>): void {
    ctx.setState({
      deviceToShare: {
        sort: 'id',
        order: 'DESC',
        searchParam: '',
        devices: null,
        currentPage: 1,
        loader: false,
        itemsPerPage: ctx.getState().deviceToShare.itemsPerPage
      },
      activeDeviceId: null,
      activeDeviceName: '',
      maxSlidesNum: ctx.getState().maxSlidesNum,
      slidesPhotos: [],
      activeDevice: null,
      sliderLoader: false,
      mapCoordinates: {
        address: '',
        lat: null,
        long: null,
      }
    });
  }
}

